import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { userManagementApi } from '../../api/userManagementApi';
import {
  DepotInput,
  TenantInput,
  UserAccessProfile,
  UserInput,
  isAppAdmin,
  isTenantAdmin,
} from '../../models/userManagement';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useUserManagementData } from '../../hooks/useUserManagementData';
import OverviewTab from './tabs/OverviewTab';
import TenantsTab from './tabs/TenantsTab';
import UsersTab from './tabs/UsersTab';
import DepotsTab from './tabs/DepotsTab';
import MyAccessTab from './tabs/MyAccessTab';

const TAB_ORDER = ['overview', 'tenants', 'users', 'depots', 'myAccess'] as const;
type TabId = (typeof TAB_ORDER)[number];

const tabLabels: Record<TabId, string> = {
  overview: 'Overview',
  tenants: 'Tenants',
  users: 'Users',
  depots: 'Depots',
  myAccess: 'My Access',
};

const getTabsForProfile = (profile: UserAccessProfile | null): TabId[] => {
  const tabs: TabId[] = [];
  if (isAppAdmin(profile) || isTenantAdmin(profile)) {
    tabs.push('overview', 'tenants', 'users', 'depots');
  }
  tabs.push('myAccess');
  return tabs;
};

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const UserManagementConsole: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, loading: profileLoading, error: profileError } = useUserProfile();
  const { tenants, users, depots, loading: dataLoading, error: dataError, refresh } = useUserManagementData(profile);
  const tabs = useMemo(() => getTabsForProfile(profile), [profile]);
  const [activeTab, setActiveTab] = useState<TabId>('myAccess');
  const [status, setStatus] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (tabs.length > 0) {
      const requestedTab = searchParams.get('tab') as TabId | null;
      const nextTab = requestedTab && TAB_ORDER.includes(requestedTab) && tabs.includes(requestedTab)
        ? requestedTab
        : tabs[0];
      setActiveTab((current) => (tabs.includes(current) && !requestedTab ? current : nextTab));
    }
  }, [tabs, searchParams]);

  const handleActionResult = (severity: 'success' | 'error', message: string) => {
    setStatus({ severity, message });
    if (severity === 'success') {
      refresh();
    }
  };

  const handleCreateTenant = async (payload: TenantInput) => {
    try {
      await userManagementApi.createTenant(payload);
      handleActionResult('success', `Tenant ${payload.name} created`);
    } catch (error) {
      console.error('Failed to create tenant', error);
      handleActionResult('error', getErrorMessage(error, 'Failed to create tenant'));
    }
  };

  const handleUpdateTenant = async (id: string, payload: TenantInput) => {
    try {
      await userManagementApi.updateTenant(id, payload);
      handleActionResult('success', 'Tenant updated');
    } catch (error) {
      console.error('Failed to update tenant', error);
      handleActionResult('error', getErrorMessage(error, 'Failed to update tenant'));
    }
  };

  const handleCreateUser = async (payload: UserInput) => {
    try {
      await userManagementApi.createUser(payload);
      handleActionResult('success', `User ${payload.email} invited`);
    } catch (error) {
      console.error('Failed to create user', error);
      handleActionResult('error', getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleUpdateUser = async (email: string, payload: UserInput) => {
    try {
      await userManagementApi.updateUser(email, payload);
      handleActionResult('success', 'User updated');
    } catch (error) {
      console.error('Failed to update user', error);
      handleActionResult('error', getErrorMessage(error, 'Failed to update user'));
    }
  };

  const handleCreateDepot = async (payload: DepotInput) => {
    try {
      await userManagementApi.createDepot(payload);
      handleActionResult('success', `Depot ${payload.name} created`);
    } catch (error) {
      console.error('Failed to create depot', error);
      handleActionResult('error', getErrorMessage(error, 'Failed to create depot'));
    }
  };

  const handleUpdateDepot = async (depotId: string, payload: DepotInput) => {
    try {
      await userManagementApi.updateDepot(depotId, payload);
      handleActionResult('success', 'Depot updated');
    } catch (error) {
      console.error('Failed to update depot', error);
      handleActionResult('error', getErrorMessage(error, 'Failed to update depot'));
    }
  };

  if (profileLoading || !profile) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        {profileError ? <Alert severity="error">{profileError}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ display: 'flex', height: '100%', bgcolor: 'background.default' }}>
      <Box width={220} borderRight="1px solid" borderColor="divider" display="flex" flexDirection="column">
        <Box px={2} py={3}>
          <Typography variant="h6">User Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {profile.name || profile.email}
          </Typography>
        </Box>
        <Divider />
        <List>
          {tabs.map((tab) => (
            <ListItemButton
              key={tab}
              selected={activeTab === tab}
              onClick={() => {
                setActiveTab(tab);
                setSearchParams({ tab });
              }}
            >
              <ListItemText primary={tabLabels[tab]} />
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Box flex={1} p={3} overflow="auto">
        {status && (
          <Alert severity={status.severity} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
            {status.message}
          </Alert>
        )}
        {dataError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {dataError}
          </Alert>
        )}

        {activeTab === 'overview' && (
          <OverviewTab tenants={tenants} users={users} depots={depots} loading={dataLoading} onRefresh={refresh} />
        )}

        {activeTab === 'tenants' && (
          <TenantsTab
            tenants={tenants}
            loading={dataLoading}
            canEdit={isAppAdmin(profile)}
            onCreateTenant={handleCreateTenant}
            onUpdateTenant={handleUpdateTenant}
          />
        )}

        {activeTab === 'users' && (
          <UsersTab
            users={users}
            tenants={tenants}
            loading={dataLoading}
            canEdit={isAppAdmin(profile) || isTenantAdmin(profile)}
            onCreateUser={handleCreateUser}
            onUpdateUser={handleUpdateUser}
          />
        )}

        {activeTab === 'depots' && (
          <DepotsTab
            depots={depots}
            tenants={tenants}
            users={users}
            loading={dataLoading}
            canEdit={isAppAdmin(profile) || isTenantAdmin(profile)}
            onCreateDepot={handleCreateDepot}
            onUpdateDepot={handleUpdateDepot}
          />
        )}

        {activeTab === 'myAccess' && <MyAccessTab profile={profile} />}
      </Box>
    </Paper>
  );
};

export default UserManagementConsole;
